// ⚠️ STUB matching `@geosoft/platform-sdk` API draft. Replace with the published
// package per RFC-001. Public surface mirrored to ease swap.

export interface CurrentUser {
  id: string;
  email: string;
  displayName: string;
  roles: string[];
  orgs: string[];
}

export interface TenantContext {
  tenantId: string;
  orgId?: string;
  classificationClearance?: 'public' | 'tenant' | 'restricted' | 'classified';
}

let _user: CurrentUser | null = null;
let _ctx: TenantContext | null = null;
let _accessToken: string | null = null;

const TOKEN_STORAGE_KEY = 'geosoft.auth.access_token';

export interface InitAuthOptions {
  keycloakUrl: string;
  realm: string;
  clientId: string;
  defaultTenant?: string;
}

type Claims = Record<string, unknown>;

function b64UrlDecode(input: string): string | null {
  const normalized = input.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4;
  const padded = normalized + (pad ? '='.repeat(4 - pad) : '');
  try {
    if (typeof atob !== 'function') return null;
    return atob(padded);
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string): Claims | null {
  const parts = token.split('.');
  if (parts.length < 2) return null;
  const payloadRaw = b64UrlDecode(parts[1]);
  if (!payloadRaw) return null;
  try {
    return JSON.parse(payloadRaw) as Claims;
  } catch {
    return null;
  }
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function asStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === 'string' && v.length > 0);
}

function extractRoles(claims: Claims): string[] {
  const direct = asStringArray(claims.roles);
  const realmAccess = claims.realm_access;
  const realmRoles =
    realmAccess && typeof realmAccess === 'object' && !Array.isArray(realmAccess)
      ? asStringArray((realmAccess as Claims).roles)
      : [];
  const unique = new Set([...direct, ...realmRoles]);
  if (unique.size === 0) unique.add('viewer');
  return [...unique];
}

function resolveTenant(claims: Claims, fallback: string): TenantContext {
  const tenantId =
    asString(claims.tenant_id) ??
    asString(claims.org_alias) ??
    asString(claims.org_id) ??
    fallback;
  const orgId = asString(claims.org_id) ?? asString(claims.org_alias) ?? undefined;
  const classification = asString(claims.classification_clearance);
  const allowed = new Set(['public', 'tenant', 'restricted', 'classified']);
  return {
    tenantId,
    orgId,
    classificationClearance:
      classification && allowed.has(classification)
        ? (classification as TenantContext['classificationClearance'])
        : undefined,
  };
}

function resolveUser(claims: Claims): CurrentUser {
  const id = asString(claims.sub) ?? 'unknown-user';
  const email = asString(claims.email) ?? `${id}@unknown.local`;
  const displayName =
    asString(claims.name) ??
    asString(claims.preferred_username) ??
    asString(claims.email) ??
    id;
  const orgs =
    asStringArray(claims.organizations).length > 0
      ? asStringArray(claims.organizations)
      : [asString(claims.org_alias) ?? 'default-org'];
  return {
    id,
    email,
    displayName,
    roles: extractRoles(claims),
    orgs,
  };
}

function readStoredToken(): string | null {
  if (_accessToken && _accessToken.trim()) return _accessToken;
  const envToken = import.meta.env.VITE_KEYCLOAK_DEV_BEARER;
  if (envToken && envToken.trim()) return envToken.trim();
  if (typeof window === 'undefined') return null;
  const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  return token && token.trim() ? token : null;
}

export async function initAuth(opts: InitAuthOptions): Promise<void> {
  const fallbackTenant = opts.defaultTenant ?? 'tnt-default';
  const token = readStoredToken();
  const claims = token ? decodeJwtPayload(token) : null;

  if (token && claims) {
    _accessToken = token;
    _user = resolveUser(claims);
    _ctx = resolveTenant(claims, fallbackTenant);
    return;
  }

  // Fallback remains for local/dev when no Keycloak token is available.
  _accessToken = null;
  _user = {
    id: 'dev-user',
    email: 'dev@geosoft.local',
    displayName: 'Dev User',
    roles: ['viewer'],
    orgs: ['geosoft-dev'],
  };
  _ctx = { tenantId: fallbackTenant };
}

export function getCurrentUser(): CurrentUser {
  if (!_user) throw new Error('initAuth not called');
  return _user;
}

export function getTenantContext(): TenantContext {
  if (!_ctx) throw new Error('initAuth not called');
  return _ctx;
}

export function requireRole(role: string): void {
  if (!_user || !_user.roles.includes(role)) {
    throw new NotAuthorizedError(`Missing required role: ${role}`);
  }
}

export async function getAccessToken(): Promise<string | null> {
  return _accessToken;
}

export function setAccessTokenForDev(token: string | null): void {
  _accessToken = token;
  if (typeof window !== 'undefined') {
    if (token && token.trim()) window.localStorage.setItem(TOKEN_STORAGE_KEY, token.trim());
    else window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export const log = {
  info: (msg: unknown, extra?: Record<string, unknown>) =>
    console.info('[platform]', msg, extra ?? ''),
  warn: (msg: unknown, extra?: Record<string, unknown>) =>
    console.warn('[platform]', msg, extra ?? ''),
  error: (msg: unknown, extra?: Record<string, unknown>) =>
    console.error('[platform]', msg, extra ?? ''),
};

export class PlatformError extends Error {}
export class NotAuthorizedError extends PlatformError {}
export class NotFoundError extends PlatformError {}
